import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Chrome } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center relative overflow-hidden px-6 bg-white" data-testid="login-page">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md bg-white border border-zinc-200 shadow-xl shadow-pink-100/50 p-10 md:p-12"
      >
        <Link to="/" data-testid="login-logo">
          <Logo size="md" />
        </Link>
        <div className="flex gap-2 mt-10 mb-8" data-testid="login-mode-tabs">
          {["login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(""); }}
              data-testid={`login-mode-${m}`}
              className={`flex-1 text-[10px] uppercase tracking-[0.3em] py-3 border transition-colors duration-300 ${
                mode === m ? "bg-gold text-white border-gold" : "border-zinc-200 text-zinc-500 hover:text-gold hover:border-gold/50"
              }`}
            >
              {m === "login" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-5">
          {mode === "register" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 block mb-2">Full Name</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="login-name-input"
                className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3.5 focus:outline-none focus:border-gold/60 transition-colors duration-300"
              />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 block mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3.5 focus:outline-none focus:border-gold/60 transition-colors duration-300"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 block mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3.5 focus:outline-none focus:border-gold/60 transition-colors duration-300"
            />
          </div>
          {error && <p className="text-red-500 text-xs tracking-wide" data-testid="login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-gold text-white text-xs uppercase tracking-[0.3em] py-4 hover:bg-champagne transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? "One moment…" : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>
        <div className="flex items-center gap-4 my-6">
          <span className="flex-1 h-px bg-zinc-200" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">or</span>
          <span className="flex-1 h-px bg-zinc-200" />
        </div>
        <button
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/account";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          data-testid="google-login-btn"
          className="w-full flex items-center justify-center gap-3 border border-zinc-300 text-zinc-700 text-xs uppercase tracking-[0.3em] py-4 hover:border-gold hover:text-gold transition-colors duration-300"
        >
          <Chrome size={15} /> Continue with Google
        </button>
        <button
          onClick={() => navigate("/shop")}
          data-testid="guest-login-btn"
          className="w-full border border-zinc-300 text-zinc-600 text-xs uppercase tracking-[0.3em] py-4 hover:border-gold hover:text-gold transition-colors duration-300"
        >
          Continue as Guest
        </button>
        <p className="text-[10px] text-zinc-400 text-center mt-6 leading-relaxed">
          Members earn {5}% wallet cashback on every order and can write reviews.
        </p>
      </motion.div>
    </main>
  );
}
